import React, { useEffect, useState } from "react";
import { AnimatedImage } from "@/components/ui/animated-image";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { LoadingIcon } from "@/components/ui/loading-icon";
import type {
  CommunityPreview,
  PublicWorkspaceMetadata,
} from "@ano/shared/api/public-workspaces";
import type { PublicWorkspacesApi } from "@/api/public-workspaces";

const PREVIEW_LOOKUP_TIMEOUT_MS = 8000;

/**
 * Pure stat-line builder shared by the public-workspace landing surfaces
 * (the signed-in splash card here + the logged-out anonymous view). Turns
 * member/channel counts into the "1,234 members · 12 channels" line, with
 * correct singular/plural and locale-grouped member counts. Zero/undefined
 * counts are omitted so a brand-new community never reads "0 members".
 */
export function buildCommunityStats(opts: {
  memberCount?: number | null;
  channelCount?: number | null;
}): string[] {
  const members = opts.memberCount ?? 0;
  const channels = opts.channelCount ?? 0;
  const out: string[] = [];
  if (members > 0) {
    out.push(`${members.toLocaleString()} member${members === 1 ? "" : "s"}`);
  }
  if (channels > 0) {
    out.push(`${channels} channel${channels === 1 ? "" : "s"}`);
  }
  return out;
}

export function CommunityIntro({ meta }: { meta: PublicWorkspaceMetadata }) {
  const featured = meta.featured_channels ?? [];
  const hasAnything = meta.tagline || featured.length > 0 || meta.rules;
  if (!hasAnything) return null;
  return (
    <div className="space-y-3">
      {meta.tagline && (
        <p className="text-sm text-foreground/90">{meta.tagline}</p>
      )}
      {featured.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Featured channels
          </p>
          <div className="flex flex-wrap gap-1.5">
            {featured.map((ch) => (
              <span
                key={ch.id}
                className="rounded-full bg-foreground/[0.06] px-2.5 py-1 text-xs"
              >
                <span className="text-muted-foreground">#</span>
                {ch.name}
              </span>
            ))}
          </div>
        </div>
      )}
      {meta.rules && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Community rules
          </p>
          <div className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md border border-border p-3 text-xs text-muted-foreground">
            {meta.rules}
          </div>
        </div>
      )}
    </div>
  );
}

export function CommunityPreviewSection({
  slug,
  name,
  api,
}: {
  slug: string;
  name: string;
  api: PublicWorkspacesApi;
}) {
  const [status, setStatus] = useState<"loading" | "loaded" | "empty">(
    "loading",
  );
  const [preview, setPreview] = useState<CommunityPreview | null>(null);

  useEffect(() => {
    let cancelled = false;
    const reset = window.setTimeout(() => {
      if (cancelled) return;
      setStatus("loading");
      setPreview(null);
    }, 0);
    const timeout = window.setTimeout(() => {
      if (!cancelled) setStatus("empty");
    }, PREVIEW_LOOKUP_TIMEOUT_MS);
    void api
      .preview(slug)
      .then((res) => {
        window.clearTimeout(reset);
        window.clearTimeout(timeout);
        if (cancelled) return;
        if (
          !res ||
          res.entry_mode !== "preview_then_join" ||
          res.channels.length === 0
        ) {
          setStatus("empty");
          return;
        }
        setPreview(res);
        setStatus("loaded");
      })
      .catch(() => {
        window.clearTimeout(reset);
        window.clearTimeout(timeout);
        if (!cancelled) setStatus("empty");
      });
    return () => {
      cancelled = true;
      window.clearTimeout(reset);
      window.clearTimeout(timeout);
    };
  }, [api, slug]);

  if (status === "loading") {
    return (
      <div className="flex justify-center py-3">
        <LoadingIcon size={18} className="text-muted-foreground" />
      </div>
    );
  }
  if (status !== "loaded" || !preview) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        A peek inside {name}
      </p>
      <div className="relative max-h-80 space-y-3 overflow-hidden rounded-lg border border-border bg-foreground/[0.02] p-3">
        {preview.channels.map((ch) => (
          <div key={ch.id} className="space-y-1.5">
            <p className="text-xs font-semibold text-foreground">
              <span className="text-muted-foreground">#</span>
              {ch.name}
            </p>
            {ch.messages.length === 0 ? (
              <p className="text-xs text-muted-foreground">Quiet so far.</p>
            ) : (
              <ul className="space-y-1">
                {ch.messages.map((m) => (
                  <li key={m.id} className="text-xs leading-relaxed">
                    <span className="font-medium text-foreground/80">
                      {m.sender_name ?? "Someone"}
                    </span>{" "}
                    <span className="text-muted-foreground">{m.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-card to-transparent" />
      </div>
      <p className="text-center text-xs text-muted-foreground">
        Join to see every channel &amp; conversation.
      </p>
    </div>
  );
}

export function SplashCard({
  slug,
  meta,
  children,
}: {
  slug: string;
  meta?: PublicWorkspaceMetadata;
  children: React.ReactNode;
}) {
  const name = meta?.name ?? slug;
  const logo = meta?.logo_url ?? null;
  const stats = buildCommunityStats({
    memberCount: meta?.member_count,
    channelCount: meta?.channel_count,
  });
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg">
        <CardHeader className="flex flex-row items-center gap-3.5 pt-6 pb-2">
          {logo && (
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-foreground/5">
              <AnimatedImage
                src={logo}
                alt={name}
                referrerPolicy="no-referrer"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          )}
          <div className="min-w-0">
            <h2 className="truncate text-xl font-semibold">{name}</h2>
            {stats.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {stats.join(" · ")}
              </p>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pb-6">{children}</CardContent>
      </Card>
    </div>
  );
}
